import moment from "moment";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import React, { useMemo } from "react";

import { CalendarMode, Row } from "../../../../../types";
import { getOriginalResource } from "../../../../helpers";
import { useBookingRowsContext } from "../../../context/booking_rows_context";
import { useCalendarContext } from "../../../context/calendar_context";
import { OverbookingsBar } from "./overbookings_bar/overbookings_bar";
import { useGroupItemsByThreshold } from "./use_group_items_by_threshold";

type OverbookingsProps = {
  row: Row;
  startDate: string;
};

export const Overbookings = ({ row, startDate }: OverbookingsProps): JSX.Element => {
  const { dayWidth } = useCalendarContext();
  const { groupItemsByThreshold } = useGroupItemsByThreshold();
  const { warningsByProfile } = useBookingRowsContext();

  const originalResourceId = getOriginalResource(row, CalendarMode.Workforce)?.id;
  const overbookings = originalResourceId ? warningsByProfile[originalResourceId]?.overbookings : [];
  const groupedOverbookings = groupItemsByThreshold(overbookings);
  const { utc } = useDateFormatter();

  const formattedOverbookings = useMemo(
    () =>
      (groupedOverbookings || []).map((group) => {
        const { overbookings } = group;
        const groupStartDate = overbookings[0].date;
        const groupEndDate = overbookings[overbookings.length - 1].date;

        const daysFromStart = utc(groupStartDate).diff(startDate, "days");
        const duration = Math.abs(
          moment(groupEndDate).endOf("day").diff(moment(groupStartDate).startOf("day"), "days") + 1
        );

        return {
          overbookings: group,
          left: daysFromStart * dayWidth,
          duration,
          start: groupStartDate,
          end: groupEndDate
        };
      }),
    [groupedOverbookings, startDate, dayWidth]
  );

  return (
    <>
      {formattedOverbookings.map((overbookingsGroup, index) => {
        const { start, end, left, duration, overbookings } = overbookingsGroup;

        return (
          <OverbookingsBar
            key={`overbooking-${originalResourceId}-${start}-${end}-${index}`}
            overbookingsWithThreshold={overbookings}
            left={left}
            duration={duration}
            start={start}
            end={end}
          />
        );
      })}
    </>
  );
};
